import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { PermissionsService } from '../../../services/permissions.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilitySharedService } from '../../../services/shared/utility-shared.service';
import { UtilityLanguageSuffix } from '../../../models/utility.model';
import { Language } from '../../../models/user.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-otp-verification',
  templateUrl: './otp-verification.component.html',
  styleUrl: './otp-verification.component.scss',
  standalone: false,
})
export class OtpVerificationComponent {
  @ViewChild('openRegisterSuccessModal') openRegisterSuccessModal!: ElementRef;
  @ViewChild('closeModal') closeRegisterSuccessModal!: ElementRef;
  @ViewChildren('otpInput0, otpInput1, otpInput2, otpInput3') otpInputs: any;

  otpDigits: string[] = ['', '', '', ''];
  otpError: string = '';
  otpExpired: boolean = false;
  otpLoading: boolean = false;
  registeredEmail: string = '';
  registeredPass: string = '';
  resendCooldown: number = 0;
  private resendInterval: any;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private router: Router,
    public permissionsService: PermissionsService,
    private utilitySharedService: UtilitySharedService,
    private translateService: TranslateService,
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.registeredEmail = navigation?.extras.state?.['email'];
    this.registeredPass = navigation?.extras.state?.['password'];
    if (this.registeredEmail == null || this.registeredPass == null)
      this.router.navigate([''], { replaceUrl: true });
  }

  onOtpDigitInput(event: any, index: number) {
    const value = event.target.value;
    if (value && !/^\d$/.test(value)) {
      this.otpDigits[index] = '';
      return;
    }
    this.otpError = '';
    if (value && index < 3) {
      const inputs = this.otpInputs.toArray();
      inputs[index + 1].nativeElement.focus();
    }
  }

  onOtpKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      const inputs = this.otpInputs.toArray();
      inputs[index - 1].nativeElement.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const paste = event.clipboardData?.getData('text') || '';
    const digits = paste.replace(/\D/g, '').substring(0, 4);
    for (let i = 0; i < 4; i++) {
      this.otpDigits[i] = digits[i] || '';
    }
    if (digits.length > 0) {
      const inputs = this.otpInputs.toArray();
      const focusIndex = Math.min(digits.length, 3);
      inputs[focusIndex].nativeElement.focus();
    }
    this.otpError = '';
  }

  verifyOtp() {
    const otpCode = this.otpDigits.join('');
    if (otpCode.length !== 4) {
      this.otpError =
        'You have entered a wrong code. Please check the code and try again';
      return;
    }
    this.otpLoading = true;
    this.otpError = '';
    this.otpExpired = false;
    var obj = {
      email: this.registeredEmail,
      otpCode: otpCode,
    };
    this.authService.verifyOTPCode(obj).subscribe({
      next: (response) => {
        this.otpLoading = false;
        if (
          (response?.status === 200 || response?.status === 'OK') &&
          response.message === 'Email verified successfully.'
        ) {
          setTimeout(() => {
            this.openRegisterSuccessModal?.nativeElement?.click();
          }, 300);
        } else {
          const msg = response?.message || 'Verification failed.';
          this.otpError = msg;
          this.otpExpired = msg.toLowerCase().includes('expired');
        }
      },
      error: (_) => {
        this.otpLoading = false;
        this.otpError = 'An error occurred. Please try again.';
      },
    });
  }

  resendOtp() {
    if (this.resendCooldown > 0 && !this.otpExpired) return;
    var obj = {
      email: this.registeredEmail,
    };
    this.authService.resendOTPCode(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (
          (response?.status === 200 || response?.status === 'OK') &&
          response?.message == 'Email Sent.'
        ) {
          this.otpError = '';
          this.otpExpired = false;
          this.otpDigits = ['', '', '', ''];
          this.startResendCooldown();
          this.toastr.success('A new verification code has been sent.');
        } else {
          this.otpError = response?.message || 'Failed to resend code.';
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred while resending code.');
      },
    });
  }

  startResendCooldown() {
    this.resendCooldown = 60;
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
    this.resendInterval = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        clearInterval(this.resendInterval);
      }
    }, 1000);
  }

  maskEmail(email: string): string {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (local.length <= 2) {
      return local[0] + '***@' + domain;
    }
    return local[0] + '***' + local[local.length - 1] + '@' + domain;
  }

  closeSuccessfullRegistrationModal() {
    this.closeRegisterSuccessModal?.nativeElement?.click();
  }

  proceedToLogin() {
    this.closeRegisterSuccessModal?.nativeElement?.click();
    this.login();
  }

  loginCommon(x: any) {
    if ((x.status === 'OK' || x.status === '200') && x.data?.token) {
      localStorage.setItem('token', x.data.token);
      localStorage.setItem('translation', JSON.stringify(x.data.translation));

      const user: any = this.authService.decodeJWTToken();
      if (user) {
        localStorage.setItem('preferredLanguage', user?.preferredLanguage);
        this.utilitySharedService.setUtilityId(
          user?.utilityId ? user?.utilityId : 0,
        );
        localStorage.setItem('isAdmin', '1');
        localStorage.setItem(
          'bpsAdminLevel',
          user?.bpsAdminLevel ? user?.bpsAdminLevel : 0,
        );
        if (this.permissionsService.checkIfhasUtility()) {
          if (x.data?.utility?.dashboardConfiguration) {
            localStorage.setItem(
              'configuration',
              JSON.stringify(x.data.utility.dashboardConfiguration),
            );
          }

          if (x.data?.utility?.validationRules) {
            localStorage.setItem(
              'validationRules',
              JSON.stringify(x.data.utility.validationRules),
            );
          }

          if (x.data?.utility?.colorCode) {
            localStorage.setItem('colorCode', x.data.utility.colorCode);
            this.setColors(x.data.utility.colorCode);
          }

          if (x.data?.utility?.imageUrl) {
            localStorage.setItem('logoUrl', x.data.utility.imageUrl);
          }
          if (x.data?.utility?.currency) {
            localStorage.setItem('currency', x.data.utility.currency);
          }
          if (x.data?.utility?.longitude) {
            localStorage.setItem('longitude', x.data.utility.longitude);
          }

          if (x.data?.utility?.latitude) {
            localStorage.setItem('latitude', x.data.utility.latitude);
          }

          if (x.data?.utility?.mapConfiguration) {
            localStorage.setItem(
              'mapConfiguration',
              JSON.stringify(x.data.utility.mapConfiguration),
            );
          }

          if (x.data?.utility?.aiMapConfiguration) {
            localStorage.setItem(
              'aiMapConfiguration',
              JSON.stringify(x.data.utility.aiMapConfiguration),
            );
          }

          localStorage.setItem(
            'languageSuffixes',
            JSON.stringify(x.data?.utility?.languageSuffixes ?? []),
          );

          this.setLanguage();

          this.permissionsService.getPermissions().subscribe({
            next: () => {
              this.permissionsService.isMapViewAllowed &&
              this.permissionsService.hasDashboardConfigMap
                ? this.router.navigate(['map'])
                : this.router.navigate(['dashboard']);
            },
            error: () => this.toastr.error('Permissions load failed'),
          });

          let notVisibleFields: string[] =
            x.data.utility.notVisibleFields || [];

          if (
            x.data?.utility?.notVisibleFields &&
            x.data?.utility?.notVisibleFields !== 'undefined'
          ) {
            localStorage.setItem(
              'notVisibleFields',
              JSON.stringify(notVisibleFields),
            );
          }
        } //bps admin user
        else {
          localStorage.setItem('longitude', '0.381388470567412');
          localStorage.setItem('latitude', '5.906986824806585');
          this.permissionsService.getPermissions().subscribe({
            next: () => {
              this.setBpsAdminColors();
              this.router.navigate(['dashboard-bps']);
            },
            error: () => this.toastr.error('Permissions load failed'),
          });
        }
      }
    } else this.toastr.error(x.message);
  }

  login() {
    this.authService
      .login(this.registeredEmail, this.registeredPass)
      .subscribe({
        next: (loginResponse) => {
          this.utilitySharedService.removeUtilityId();
          this.loginCommon(loginResponse);
        },
        error: () => {
          this.toastr.error('An error occurred.');
        },
      });
  }

  setBpsAdminColors() {
    document.documentElement.style.setProperty('--primary-color', '#03423E');
    document.documentElement.style.setProperty('--bg-color', '#F5FBFA');
    document.documentElement.style.setProperty(
      '--table-root-bg-color',
      '#EBF1F1',
    );
    document.documentElement.style.setProperty('--card-colr', '#e4ecea');
    document.documentElement.style.setProperty('--bubble-color', '#688F8D');
  }

  setColors(color: string) {
    document.documentElement.style.setProperty('--primary-color', color);
    document.documentElement.style.setProperty('--card-color', color + '22');
    document.documentElement.style.setProperty('--bg-color', color + '08');
    document.documentElement.style.setProperty('--bubble-color', color + '95');
    document.documentElement.style.setProperty('--bubble-color', color + '95');
  }
  setLanguage() {
    const langIndex = parseInt(
      localStorage.getItem('preferredLanguage') ?? '0',
      10,
    );
    const suffixes = this.getLanguageSuffixes();

    const translationFileName = this.getTranslationFileName(
      langIndex,
      suffixes,
    );
    this.translateService.setDefaultLang('en');
    this.translateService.use(translationFileName);
  }
  getLanguageSuffixes(): UtilityLanguageSuffix[] {
    return JSON.parse(localStorage.getItem('languageSuffixes') ?? '[]');
  }
  getTranslationFileName(
    langIndex: number,
    suffixes: UtilityLanguageSuffix[],
  ): string {
    const fileName = langIndex === Language.English ? 'en' : 'fr';
    const suffix = suffixes.find((x) => x.language === langIndex)?.suffix || '';
    return `${fileName}${suffix}`;
  }
}
